import { Tabs } from "antd";
import { useEffect } from "react";
import { FieldValues } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { ContentWrapper } from "~/Components";
import { DynamicForm, FormConfig } from "~/Components/DynamicForm/DynamicForm";
import { useHasAccess } from "~/Hooks/useHasAccess";
import { useNotification } from "~/Hooks/useNotification";
import { useQuery } from "~/Hooks/useQuery";
import { useDocumentDetails } from "~/Modules/ECMS/MAGP/ApprovalManagement/Drilldown.Hooks";
import { post, put } from "~/Services";
import { decodeHTML } from "~/Utils/CommonUtils";
import { corporateHost } from "~/Utils";
import { fields } from "../constants";
import { useManagementIntegrity } from "../CorporateIntegrity.hook";

export const ManagementIntegrityMessagePageContent = () => {
  const navigate = useNavigate();
  const query = useQuery();
  const { openToast } = useNotification();
  const { hasRole } = useHasAccess();
  const { data: corporateIntegrityById, isLoading: isLoadingCorporateIntegrityById, refetch: refetchCorporateIntegrityById } = useDocumentDetails(
    query?.get("id") || ""
  );  
  const { data: managementData, isLoading: isLoadingManagementIntegrity, refetch: refetchManagementIntegrity } = useManagementIntegrity();

  useEffect(()=>{
    if(!query.get('id')){
      refetchManagementIntegrity();
    }
  },[])

  useEffect(()=>{
    if(query.get('id')){
      refetchCorporateIntegrityById();
    }
  },[query.get('id')])

  const mDataById = {
    mEn: JSON.parse(
      corporateIntegrityById?.payload?.field_content_english ? corporateIntegrityById?.payload?.field_content_english : "{}"
    ),
    mMs: JSON.parse(
      corporateIntegrityById?.payload?.field_content_malay ? corporateIntegrityById?.payload?.field_content_malay : "{}"
    ),
    mZh: JSON.parse(
      corporateIntegrityById?.payload?.field_content_mandarin ? corporateIntegrityById?.payload?.field_content_mandarin : "{}"
    ),
  };

  const ManagementIntegrityMessageConfig: FormConfig = {
    initialValues: {
      field_content_english_image:
        mDataById?.mEn?.data
          ? decodeHTML([
              {
                uid: "-1",
                name: "WIC-Structure_image.png",
                status: "done",
                url: mDataById?.mEn?.data?.image,
              },
            ])
          : managementData?.[0]?.field_content_english?.data?.image
            ? decodeHTML([
                {
                  uid: "-1",
                  name: "Management-Integrity-Message_image.png",
                  status: "done",
                  url: managementData?.[0]?.field_content_english?.data?.image,
                },
              ])
            : "",
      field_content_english_text: mDataById?.mEn?.data
        ? decodeHTML(mDataById?.mEn?.data?.text)
        : decodeHTML(managementData?.[0]?.field_content_english?.data?.text),
      field_content_english_next_page_name: mDataById?.mEn?.data
        ? decodeHTML(mDataById?.mEn?.data?.nextPage?.name)
        : decodeHTML(managementData?.[0]?.field_content_english?.data?.nextPage?.name),
      field_content_english_next_page_link: mDataById?.mEn?.data
        ? decodeHTML(mDataById?.mEn?.data?.nextPage?.link)
        : decodeHTML(managementData?.[0]?.field_content_english?.data?.nextPage?.link),
      field_content_ms_image:
        mDataById?.mMs?.data
          ? decodeHTML([
              {
                uid: "-1",
                name: "WIC-Structure_image.png",
                status: "done",
                url: mDataById?.mMs?.data?.image,
              },
            ])
          : managementData?.[0]?.field_content_malay?.data?.image
            ? decodeHTML([
                {
                  uid: "-1",
                  name: "Management-Integrity-Message_image.png",
                  status: "done",
                  url: managementData?.[0]?.field_content_malay?.data?.image,
                },
              ])
            : "",
      field_content_ms_text: mDataById?.mMs?.data
        ? decodeHTML(mDataById?.mMs?.data?.text)
        : decodeHTML(managementData?.[0]?.field_content_malay?.data?.text),
      field_content_ms_next_page_name: mDataById?.mMs?.data
        ? decodeHTML(mDataById?.mMs?.data?.nextPage?.name)
        : decodeHTML(managementData?.[0]?.field_content_malay?.data?.nextPage?.name),
      field_content_ms_next_page_link: mDataById?.mMs?.data
        ? decodeHTML(mDataById?.mMs?.data?.nextPage?.link)
        : decodeHTML(managementData?.[0]?.field_content_malay?.data?.nextPage?.link),
      field_content_zh_image:
        mDataById?.mZh?.data
          ? decodeHTML([
              {
                uid: "-1",
                name: "WIC-Structure_image.png",
                status: "done",
                url: mDataById?.mZh?.data?.image,
              },
            ])
          : managementData?.[0]?.field_content_mandarin?.data?.image
            ? decodeHTML([
                {
                  uid: "-1",
                  name: "Management-Integrity-Message_image.png",
                  status: "done",
                  url: managementData?.[0]?.field_content_mandarin?.data?.image,
                },
              ])
            : "",
      field_content_zh_text: mDataById?.mZh?.data
        ? decodeHTML(mDataById?.mZh?.data?.text)
        : decodeHTML(managementData?.[0]?.field_content_mandarin?.data?.text),
      field_content_zh_next_page_name: mDataById?.mZh?.data
        ? decodeHTML(mDataById?.mZh?.data?.nextPage?.name)
        : decodeHTML(managementData?.[0]?.field_content_mandarin?.data?.nextPage?.name),
      field_content_zh_next_page_link: mDataById?.mZh?.data
        ? decodeHTML(mDataById?.mZh?.data?.nextPage?.link)
        : decodeHTML(managementData?.[0]?.field_content_mandarin?.data?.nextPage?.link),
    },
    fields: fields.managementIntegrityMessagePageContent,
  };

  const onSubmit = async (
    data: FieldValues,
    apiUrl: string,
    action: string,
    method: string,
    status: "DRAFT" | "PENDING",
    reviewNotes?: string
  ) => {
    let excludedKeys: any = [];
    let imageKeys: any = [
      "field_content_english_image",
      "field_content_ms_image",
      "field_content_zh_image",
      "field_content_english_wicStructure_image",
      "field_content_english_procedure_image",
      "field_content_ms_wicStructure_image",
      "field_content_ms_procedure_image",
      "field_content_zh_wicStructure_image",
      "field_content_zh_procedure_image",
    ];
    for (const key of imageKeys) {
      try {
        let field = data[key];
        if (field[0].originFileObj) {
          const formData = new FormData();
          formData.append("upload-file", field[0].originFileObj);
          formData.append("application", "CORPORATE");
          const res: any = await post("management/upload-file", formData);

          if (res) {
            data[key] = res.response.data;
          }
        } else {
          data[key] = data[key][0].url;
        }
      } catch (error) {}
    }

    const payload = {
      action: action,
      method: method,
      module: "Corporate-integrity",
      subModule: `${query?.get("activeTab")}_${query.get('subActiveTab')}`,
      type: "DEDICATED",
      application: "MAHB_CORPORATE",
      reviewNotes: reviewNotes,
      appList: [
        {
          appName: "MAHB_CORPORATE",
          apiUrl: apiUrl,
        },
      ],
      moduleKey: "MAHB_CORPORATE_Corporate-integrity_Management Integrity Message",
      payload: {
        id: data?.id || managementData?.[0]?.id || corporateIntegrityById?.payload?.id,
        field_content_english: JSON.stringify(
          query?.get("activeTab") === "managementIntegrityMessage"
            ? {
                data: {
                  image: data?.field_content_english_image ?? "",
                  text: data?.field_content_english_text ?? "",
                  nextPage: {
                    name: data?.field_content_english_next_page_name ?? "",
                    link: data?.field_content_english_next_page_link ?? "",
                  },
                },
                labelData: {},
              }
            : query?.get("activeTab") === "antiBriberyCorruption"
              ? {
                  data: {
                    title: data?.field_content_english_title ?? "",
                    extroText: data?.field_content_english_extroText ?? "",
                    description: data?.field_content_english_description ?? "",
                    introText: data?.field_content_english_introText ?? "",
                    nextPage: {
                      name: data?.field_content_english_next_page_name ?? "",
                      link: data?.field_content_english_next_page_link ?? "",
                    },
                  },
                  labelData: {},
                }
              : query?.get("activeTab") === "whistleblowingProgram"
                ? {
                    data: {
                      title: data?.field_content_english_title ?? "",
                      descriptions: data?.field_content_english_descriptions ?? "",
                      wicStructure: {
                        title: data?.field_content_english_wicStructure_title ?? "",
                        image: data?.field_content_english_wicStructure_image ?? "",
                      },
                      procedure: {
                        title: data?.field_content_english_procedure_title ?? "",
                        image: data?.field_content_english_procedure_image ?? "",
                      },
                      nextPage: {
                        name: data?.field_content_english_next_page_name ?? "",
                        link: data?.field_content_english_next_page_link ?? "",
                      },
                    },
                  }
                : query?.get("activeTab") === "whistleblowingPolicy"
                  ? {
                      data: {
                        markup: data?.field_content_english_markup ?? "",
                        nextPage: {
                          name: data?.field_content_english_next_page_name ?? "",
                          link: data?.field_content_english_next_page_link ?? "",
                        },
                      },
                    }
                  : {
                      data: {
                        title: data?.field_content_english_title ?? "",
                        description: data?.field_content_english_description ?? "",
                        afterSubmit: {
                          succesMessage: data?.field_content_english_after_submit_succesMessage ?? "",
                          appreciationMessage: data?.field_content_english_after_submit_appreciationMessage ?? "",
                          backToForm: data?.field_content_english_after_submit_backToForm ?? "",
                        },
                        nextPage: {
                          name: data?.field_content_english_next_page_name ?? "",
                          link: data?.field_content_english_next_page_link ?? "",
                        },
                      },
                      labelData: {
                        persons: {
                          titleText: data?.field_content_english_persons_titleText ?? "",
                          nameFieldText: data?.field_content_english_persons_nameFieldText ?? "",
                          designationFieldText: data?.field_content_english_persons_designationFieldText ?? "",
                          divisionOrUnitFieldText: data?.field_content_english_persons_divisionOrUnitFieldText ?? "",
                          companyOrAirportFieldText: data?.field_content_english_persons_companyOrAirportFieldText ?? "",
                        },
                        concerns: {
                          titleText: data?.field_content_english_concerns_titleText ?? "",
                          dateTimeFieldText: data?.field_content_english_concerns_dateTimeFieldText ?? "",
                          locationOfIncidentFieldText: data?.field_content_english_concerns_locationOfIncidentFieldText ?? "",
                          detailsFieldText: data?.field_content_english_concerns_detailsFieldText ?? "",
                          otherPartiesInvolvedFieldText:
                            data?.field_content_english_concerns_otherPartiesInvolvedFieldText ?? "",
                          namesOfWitnessesInvolvedFieldText:
                            data?.field_content_english_concerns_namesOfWitnessesInvolvedFieldText ?? "",
                        },
                        yourParticulars: {
                          titleText: data?.field_content_english_yourParticulars_titleText ?? "",
                          fullnameFieldText: data?.field_content_english_yourParticulars_fullnameFieldText ?? "",
                          staffNoFieldText: data?.field_content_english_yourParticulars_staffNoFieldText ?? "",
                          contactNoFieldText: data?.field_content_english_yourParticulars_contactNoFieldText ?? "",
                          emailFieldText: data?.field_content_english_yourParticulars_emailFieldText ?? "",
                          companyNameFieldText: data?.field_content_english_yourParticulars_companyNameFieldText ?? "",
                        },
                        confidentialityText: data?.field_content_english_confidentialityText ?? "",
                        captcha: {
                          titleText: data?.field_content_english_captcha_titleText ?? "",
                          helpText: data?.field_content_english_captcha_helpText ?? "",
                          newCaptchaText: data?.field_content_english_captcha_newCaptchaText ?? "",
                        },
                        submitButtonText: data?.field_content_english_submitButtonText ?? "",
                      },
                    }
        ),
        field_content_malay: JSON.stringify(
          query?.get("activeTab") === "managementIntegrityMessage"
            ? {
                data: {
                  image: data?.field_content_english_image ?? "",
                  text: data?.field_content_ms_text ?? "",
                  nextPage: {
                    name: data?.field_content_ms_next_page_name ?? "",
                    link: data?.field_content_ms_next_page_link ?? "",
                  },
                },
                labelData: {},
              }
            : query?.get("activeTab") === "antiBriberyCorruption"
              ? {
                  data: {
                    title: data?.field_content_ms_title ?? "",
                    extroText: data?.field_content_ms_extroText ?? "",
                    description: data?.field_content_ms_description ?? "",
                    introText: data?.field_content_ms_introText ?? "",
                    nextPage: {
                      name: data?.field_content_ms_next_page_name ?? "",
                      link: data?.field_content_ms_next_page_link ?? "",
                    },
                  },
                  labelData: {},
                }
              : query?.get("activeTab") === "whistleblowingProgram"
                ? {
                    data: {
                      title: data?.field_content_ms_title ?? "",
                      descriptions: data?.field_content_ms_descriptions ?? "",
                      wicStructure: {
                        title: data?.field_content_ms_wicStructure_title ?? "",
                        image: data?.field_content_english_wicStructure_image ?? "",
                      },
                      procedure: {
                        title: data?.field_content_ms_procedure_title ?? "",
                        image: data?.field_content_english_procedure_image ?? "",
                      },
                      nextPage: {
                        name: data?.field_content_ms_next_page_name ?? "",
                        link: data?.field_content_ms_next_page_link ?? "",
                      },
                    },
                  }
                : query?.get("activeTab") === "whistleblowingPolicy"
                  ? {
                      data: {
                        markup: data?.field_content_ms_markup ?? "",
                        nextPage: {
                          name: data?.field_content_ms_next_page_name ?? "",
                          link: data?.field_content_ms_next_page_link ?? "",
                        },
                      },
                    }
                  : {
                      data: {
                        title: data?.field_content_ms_title ?? "",
                        description: data?.field_content_ms_description ?? "",
                        afterSubmit: {
                          succesMessage: data?.field_content_ms_after_submit_succesMessage ?? "",
                          appreciationMessage: data?.field_content_ms_after_submit_appreciationMessage ?? "",
                          backToForm: data?.field_content_ms_after_submit_backToForm ?? "",
                        },
                        nextPage: {
                          name: data?.field_content_ms_next_page_name ?? "",
                          link: data?.field_content_ms_next_page_link ?? "",
                        },
                      },
                      labelData: {
                        persons: {
                          titleText: data?.field_content_ms_persons_titleText ?? "",
                          nameFieldText: data?.field_content_ms_persons_nameFieldText ?? "",
                          designationFieldText: data?.field_content_ms_persons_designationFieldText ?? "",
                          divisionOrUnitFieldText: data?.field_content_ms_persons_divisionOrUnitFieldText ?? "",
                          companyOrAirportFieldText: data?.field_content_ms_persons_companyOrAirportFieldText ?? "",
                        },
                        concerns: {
                          titleText: data?.field_content_ms_concerns_titleText ?? "",
                          dateTimeFieldText: data?.field_content_ms_concerns_dateTimeFieldText ?? "",
                          locationOfIncidentFieldText: data?.field_content_ms_concerns_locationOfIncidentFieldText ?? "",
                          detailsFieldText: data?.field_content_ms_concerns_detailsFieldText ?? "",
                          otherPartiesInvolvedFieldText:
                            data?.field_content_ms_concerns_otherPartiesInvolvedFieldText ?? "",
                          namesOfWitnessesInvolvedFieldText:
                            data?.field_content_ms_concerns_namesOfWitnessesInvolvedFieldText ?? "",
                        },
                        yourParticulars: {
                          titleText: data?.field_content_ms_yourParticulars_titleText ?? "",
                          fullnameFieldText: data?.field_content_ms_yourParticulars_fullnameFieldText ?? "",
                          staffNoFieldText: data?.field_content_ms_yourParticulars_staffNoFieldText ?? "",
                          contactNoFieldText: data?.field_content_ms_yourParticulars_contactNoFieldText ?? "",
                          emailFieldText: data?.field_content_ms_yourParticulars_emailFieldText ?? "",
                          companyNameFieldText: data?.field_content_ms_yourParticulars_companyNameFieldText ?? "",
                        },
                        confidentialityText: data?.field_content_ms_confidentialityText ?? "",
                        captcha: {
                          titleText: data?.field_content_ms_captcha_titleText ?? "",
                          helpText: data?.field_content_ms_captcha_helpText ?? "",
                          newCaptchaText: data?.field_content_ms_captcha_newCaptchaText ?? "",
                        },
                        submitButtonText: data?.field_content_ms_submitButtonText ?? "",
                      },
                    }
        ),
        field_content_mandarin: JSON.stringify(
          query?.get("activeTab") === "managementIntegrityMessage"
            ? {
                data: {
                  image: data?.field_content_english_image ?? "",
                  text: data?.field_content_zh_text ?? "",
                  nextPage: {
                    name: data?.field_content_zh_next_page_name ?? "",
                    link: data?.field_content_zh_next_page_link ?? "",
                  },
                },
                labelData: {},
              }
            : query?.get("activeTab") === "antiBriberyCorruption"
              ? {
                  data: {
                    title: data?.field_content_zh_title ?? "",
                    extroText: data?.field_content_zh_extroText ?? "",
                    description: data?.field_content_zh_description ?? "",
                    introText: data?.field_content_zh_introText ?? "",
                    nextPage: {
                      name: data?.field_content_zh_next_page_name ?? "",
                      link: data?.field_content_zh_next_page_link ?? "",
                    },
                  },
                  labelData: {},
                }
              : query?.get("activeTab") === "whistleblowingProgram"
                ? {
                    data: {
                      title: data?.field_content_zh_title ?? "",
                      descriptions: data?.field_content_zh_descriptions ?? "",
                      wicStructure: {
                        title: data?.field_content_zh_wicStructure_title ?? "",
                        image: data?.field_content_english_wicStructure_image ?? "",
                      },
                      procedure: {
                        title: data?.field_content_zh_procedure_title ?? "",
                        image: data?.field_content_english_procedure_image ?? "",
                      },
                      nextPage: {
                        name: data?.field_content_zh_next_page_name ?? "",
                        link: data?.field_content_zh_next_page_link ?? "",
                      },
                    },
                  }
                : query?.get("activeTab") === "whistleblowingPolicy"
                  ? {
                      data: {
                        markup: data?.field_content_zh_markup ?? "",
                        nextPage: {
                          name: data?.field_content_zh_next_page_name ?? "",
                          link: data?.field_content_zh_next_page_link ?? "",
                        },
                      },
                    }
                  : {
                      data: {
                        title: data?.field_content_zh_title ?? "",
                        description: data?.field_content_zh_description ?? "",
                        afterSubmit: {
                          succesMessage: data?.field_content_zh_after_submit_succesMessage ?? "",
                          appreciationMessage: data?.field_content_zh_after_submit_appreciationMessage ?? "",
                          backToForm: data?.field_content_zh_after_submit_backToForm ?? "",
                        },
                        nextPage: {
                          name: data?.field_content_zh_next_page_name ?? "",
                          link: data?.field_content_zh_next_page_link ?? "",
                        },
                      },
                      labelData: {
                        persons: {
                          titleText: data?.field_content_zh_persons_titleText ?? "",
                          nameFieldText: data?.field_content_zh_persons_nameFieldText ?? "",
                          designationFieldText: data?.field_content_zh_persons_designationFieldText ?? "",
                          divisionOrUnitFieldText: data?.field_content_zh_persons_divisionOrUnitFieldText ?? "",
                          companyOrAirportFieldText: data?.field_content_zh_persons_companyOrAirportFieldText ?? "",
                        },
                        concerns: {
                          titleText: data?.field_content_zh_concerns_titleText ?? "",
                          dateTimeFieldText: data?.field_content_zh_concerns_dateTimeFieldText ?? "",
                          locationOfIncidentFieldText:
                            data?.field_content_zh_concerns_locationOfIncidentFieldText ?? "",
                          detailsFieldText: data?.field_content_zh_concerns_detailsFieldText ?? "",
                          otherPartiesInvolvedFieldText:
                            data?.field_content_zh_concerns_otherPartiesInvolvedFieldText ?? "",
                          namesOfWitnessesInvolvedFieldText:
                            data?.field_content_zh_concerns_namesOfWitnessesInvolvedFieldText ?? "",
                        },
                        yourParticulars: {
                          titleText: data?.field_content_zh_yourParticulars_titleText ?? "",
                          fullnameFieldText: data?.field_content_zh_yourParticulars_fullnameFieldText ?? "",
                          staffNoFieldText: data?.field_content_zh_yourParticulars_staffNoFieldText ?? "",
                          contactNoFieldText: data?.field_content_zh_yourParticulars_contactNoFieldText ?? "",
                          emailFieldText: data?.field_content_zh_yourParticulars_emailFieldText ?? "",
                          companyNameFieldText: data?.field_content_zh_yourParticulars_companyNameFieldText ?? "",
                        },
                        confidentialityText: data?.field_content_zh_confidentialityText ?? "",
                        captcha: {
                          titleText: data?.field_content_zh_captcha_titleText ?? "",
                          helpText: data?.field_content_zh_captcha_helpText ?? "",
                          newCaptchaText: data?.field_content_zh_captcha_newCaptchaText ?? "",
                        },
                        submitButtonText: data?.field_content_zh_submitButtonText ?? "",
                      },
                    }
        ),
        field_page: managementData?.[0]?.field_page || corporateIntegrityById?.payload?.field_page || "",
        published: true,
      },
      status: status,
    };

    if (query.get("id")) {
      put(`/management/update-document?approvalId=${query.get("id")}`, payload)
        .then((response: any) => {
          openToast({
            content: status == "PENDING" ? "Data Sent for Review" : "Data Saved Successfully",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    } else {
      post("/management/publish", payload)
        .then((response: any) => {
          openToast({
            content: status == "DRAFT" ? "Data saved successfully" : "Data Sent for Review",
            type: "success",
          });
          if (response.status === "OK" && status == "PENDING") {
            navigate("/dedicated/status-information?activeTab=pending");
          } else {
            navigate("/dedicated/status-information?activeTab=draft");
          }
        })
        .catch((err: any) => {
          openToast({
            content: "Failed to save data",
            type: "error",
          });
        });
    }
  };

  return (
    <DynamicForm
        isLoading={
          isLoadingManagementIntegrity ||
          isLoadingCorporateIntegrityById
        }
          config={ManagementIntegrityMessageConfig}
          onSubmit={(data, buttonValue, reviewNotes) =>
            onSubmit(
              data,
              `${corporateHost}/api/page-content/${managementData?.[0]?.id || corporateIntegrityById?.payload?.id || '80'}`,
              "UPDATE",
              "PATCH",
              buttonValue == "save" ? "DRAFT" : "PENDING",
              reviewNotes
            )
          }
          defaultReviewNotes="Kindly review this Corporate Integrity"
    />
  );
};
